import { useState } from "react";
import { Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogClose,
} from "@/components/ui/dialog";
import { useCreateRoomMutation } from "@/hooks/mutation/estimates/useCreateRoomMutation";
import { useAnalytics } from "@/analytics";

interface CreateRoomDialogProps {
  estimateId: string;
  existingRoomNames?: string[];
  disabled?: boolean;
  onCreated?: (roomName: string) => void;
}

export const CreateRoomDialog = ({ estimateId, existingRoomNames = [], disabled, onCreated }: CreateRoomDialogProps) => {
  const [open, setOpen] = useState(false);
  const [roomName, setRoomName] = useState("");
  const { mutateAsync: createRoom, isPending } = useCreateRoomMutation();
  const { trackEvent, ANALYTICS_EVENTS } = useAnalytics();

  const isDuplicate = roomName.trim().length > 0 && existingRoomNames.some(
    (name) => name.toLowerCase() === roomName.trim().toLowerCase(),
  );

  const handleSubmit = async () => {
    const trimmed = roomName.trim();
    if (!trimmed) return;

    try {
      await createRoom({ estimateId, roomName: trimmed });
      trackEvent(ANALYTICS_EVENTS.ROOM_CREATED, { estimate_id: estimateId, room_name: trimmed });
      setRoomName("");
      setOpen(false);
      onCreated?.(trimmed);
    } catch {
      // onError in mutation handles toast
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && roomName.trim() && !isPending && !isDuplicate) {
      handleSubmit();
    }
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => {
      if (!isOpen) {
        trackEvent(ANALYTICS_EVENTS.ROOM_CREATE_CANCELLED, { estimate_id: estimateId });
        setRoomName("");
      }
      setOpen(isOpen);
    }}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" className="mr-2 shrink-0" disabled={disabled} onClick={() => {
          trackEvent(ANALYTICS_EVENTS.ROOM_CREATE_INITIATED, { estimate_id: estimateId });
        }}>
          <Plus className="mr-1 h-4 w-4" />
          Add Room
        </Button>
      </DialogTrigger>
      <DialogContent className="bg-background">
        <DialogHeader>
          <DialogTitle>Create Room</DialogTitle>
          <DialogDescription>Add a new room to this estimate.</DialogDescription>
        </DialogHeader>
        <div>
          <Input
            placeholder="Room name"
            value={roomName}
            onChange={(e) => setRoomName(e.target.value)}
            onKeyDown={handleKeyDown}
            autoFocus
          />
          {isDuplicate && (
            <p className="mt-1 text-sm text-destructive">A room with this name already exists.</p>
          )}
        </div>
        <DialogFooter>
          <DialogClose asChild>
            <Button variant="outline">
              Cancel
            </Button>
          </DialogClose>
          <Button onClick={handleSubmit} disabled={!roomName.trim() || isPending || isDuplicate}>
            {isPending ? "Creating..." : "Create"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
